import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api, apiError } from "@/lib/api";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const textareaClass = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function ShipmentActions({ shipment, onAction }) {
  const today = new Date().toISOString().slice(0, 10);
  const status = shipment.status;
  const actions = shipment.actions || {};
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [crateId, setCrateId] = useState(actions.dedicated_crate?.id || actions.empty_crates?.[0]?.id || "");
  const [tested, setTested] = useState(true);
  const [note, setNote] = useState("");
  const [tracking, setTracking] = useState("");
  const [date, setDate] = useState("");
  const [expectedDate, setExpectedDate] = useState(shipment.expected_delivery_date || "");
  const [replacementId, setReplacementId] = useState("");

  async function run(fn) {
    setError(null); setPending(true);
    try {
      await fn();
      setNote(""); setTracking(""); setDate("");
      onAction && onAction();
    } catch (err) { setError(apiError(err)); } finally { setPending(false); }
  }

  async function updateExpected() {
    await run(() => api.put(`/shipments/${shipment.id}/expected-delivery`, { expected_delivery_date: expectedDate || null }));
  }

  const spinner = pending && <Loader2 className="h-4 w-4 animate-spin" />;
  let body = null, title = "Next Step", description = "";

  if (status === "Created") {
    title = "Pack Shipment";
    description = "Assign an empty wooden crate box. For T-Mobile DSKs: unpack, test, and repack before shipping.";
    const ded = actions.dedicated_crate;
    const empties = actions.empty_crates || [];
    body = (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Wooden Crate</Label>
          <select className={selectClass} value={crateId} onChange={(e) => setCrateId(e.target.value)}>
            {!ded && empties.length === 0 && <option value="">No empty crates for this client</option>}
            {ded && (
              <option value={ded.id} disabled={!["Empty in Warehouse", "In Warehouse with DSK"].includes(ded.status)}>
                {ded.crate_code} — dedicated crate{!["Empty in Warehouse", "In Warehouse with DSK"].includes(ded.status) ? ` (${ded.status})` : ""}
              </option>
            )}
            {empties.map((c) => <option key={c.id} value={c.id}>{c.crate_code}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={tested} onChange={(e) => setTested(e.target.checked)} className="h-4 w-4 rounded border-input" />
          DSK tested before shipping (unpack → test → repack)
        </label>
        <div className="space-y-2">
          <Label>Note (optional)</Label>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={textareaClass} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button disabled={pending || !crateId} onClick={() => run(() => api.post(`/shipments/${shipment.id}/pack`, { crate_id: crateId, tested, note }))}>
            {spinner} {tested ? "Test Passed — Pack" : "Pack (No Test)"}
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => run(() => api.post(`/shipments/${shipment.id}/fail-test`, { note }))}>
            Test Failed
          </Button>
        </div>
      </div>
    );
  } else if (status === "Packed") {
    title = "Ship It";
    description = "Enter the carrier tracking number to mark as shipped.";
    body = (
      <div className="space-y-4">
        <div className="space-y-2"><Label>Outbound Tracking Number *</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="1Z999AA10123456784" /></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2"><Label>Shipped Date</Label><Input type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="space-y-2"><Label>Expected Delivery Date</Label><Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} data-testid="expected-delivery-input" /></div>
        </div>
        <Button disabled={pending || !tracking.trim()} onClick={() => run(() => api.post(`/shipments/${shipment.id}/ship`, { tracking, shipped_date: date, expected_delivery_date: expectedDate || null }))}>
          {spinner} Mark Shipped
        </Button>
      </div>
    );
  } else if (status === "Shipped") {
    title = "Confirm Delivery";
    description = "Mark the shipment as delivered to the store. You can also adjust the expected delivery date here.";
    body = (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Expected Delivery Date</Label>
          <div className="flex gap-2">
            <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} data-testid="expected-delivery-input" />
            <Button type="button" variant="outline" size="sm" disabled={pending} onClick={updateExpected} data-testid="save-expected-delivery">{spinner} Save</Button>
          </div>
          {shipment.expected_delivery_date && (<p className="text-xs text-muted-foreground">Currently set to {shipment.expected_delivery_date}</p>)}
        </div>
        <div className="space-y-2"><Label>Delivered Date</Label><Input type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <Button disabled={pending} onClick={() => run(() => api.post(`/shipments/${shipment.id}/deliver`, { date }))} data-testid="mark-delivered-btn">{spinner} Mark Delivered</Button>
      </div>
    );
  } else if (status === "Delivered") {
    title = "Confirm Installation";
    description = "Store opens the crate and installs the product. The empty crate starts waiting for return.";
    body = (
      <div className="space-y-4">
        <div className="space-y-2"><Label>Installed Date</Label><Input type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <Button disabled={pending} onClick={() => run(() => api.post(`/shipments/${shipment.id}/install`, { date }))}>{spinner} Mark Installed</Button>
      </div>
    );
  } else if (status === "Waiting for Box Return") {
    title = "Start Crate Return";
    description = "Enter the return tracking number for the empty crate.";
    body = (
      <div className="space-y-4">
        <div className="space-y-2"><Label>Return Tracking Number *</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} /></div>
        <Button disabled={pending || !tracking.trim()} onClick={() => run(() => api.post(`/shipments/${shipment.id}/box-return-transit`, { tracking }))}>{spinner} Mark Return In Transit</Button>
      </div>
    );
  } else if (status === "Box Return In Transit") {
    title = "Check In Returned Crate";
    description = "The crate arrived back at the Dallas warehouse.";
    body = <Button disabled={pending} onClick={() => run(() => api.post(`/shipments/${shipment.id}/box-returned`))}>{spinner} Crate Received at Warehouse</Button>;
  } else if (status === "Box Returned") {
    title = "Close Shipment";
    description = "Crate inspected and back in stock. Product stays installed at the store.";
    body = <Button disabled={pending} onClick={() => run(() => api.post(`/shipments/${shipment.id}/close`))}>{spinner} Close Shipment</Button>;
  } else if (status === "Issue / Exception") {
    title = "Resolve Exception";
    description = "Assign a replacement product (after a failed test) or cancel the shipment.";
    body = (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Replacement Product</Label>
          <select className={selectClass} value={replacementId} onChange={(e) => setReplacementId(e.target.value)}>
            <option value="">Select available product...</option>
            {(actions.available_products || []).map((p) => <option key={p.id} value={p.id}>{p.serial_number}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button disabled={pending || !replacementId} onClick={() => run(() => api.post(`/shipments/${shipment.id}/replace-product`, { product_id: replacementId }))}>
            {spinner} Assign Replacement
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => { if (window.confirm("Cancel this shipment?")) run(() => api.post(`/shipments/${shipment.id}/cancel`, { note })); }}>
            Cancel Shipment
          </Button>
        </div>
      </div>
    );
  } else if (status === "Closed") return null;

  return (
    <Card className="border-primary/30">
      <CardHeader><CardTitle className="text-base">{title}</CardTitle>{description && <CardDescription>{description}</CardDescription>}</CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
        {body}
        {!["Issue / Exception", "Closed"].includes(status) && (
          <div className="border-t pt-4">
            <details>
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">Report an issue / cancel</summary>
              <div className="mt-3 space-y-3">
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Describe the issue..." className={textareaClass} />
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" disabled={pending || !note.trim()} onClick={() => run(() => api.post(`/shipments/${shipment.id}/exception`, { note }))}>Report Issue</Button>
                  {["Created", "Packed"].includes(status) && (
                    <Button variant="destructive" size="sm" disabled={pending} onClick={() => { if (window.confirm("Cancel this shipment?")) run(() => api.post(`/shipments/${shipment.id}/cancel`, { note })); }}>Cancel Shipment</Button>
                  )}
                </div>
              </div>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
